import { createContext, useContext, useState, ReactNode } from 'react';
import { Modal } from 'antd';
import { IssueSelection } from './IssueSelection';
import { ProductSelection } from './ProductSelection';
import { RefundForm } from './RefundForm';
import { LineItem } from '@/infrastructure/domain/lineItem';

interface RefundModalContextType {
  openRefundModal: (orderId: string) => void;
  closeRefundModal: () => void;
}

const RefundModalContext = createContext<RefundModalContextType | null>(null);

export const useRefundModal = () => useContext(RefundModalContext)!;

export const RefundModalProvider = ({ children }: { children: ReactNode }) => {
  const [visible, setVisible] = useState(false);

  const [orderId, setOrderId] = useState<string | undefined>(undefined);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedIssue, setSelectedIssue] = useState<string | null>(null);
  const [selectedLineItems, setSelectedLineItems] = useState<LineItem[]>([]);

  const goToNext = () => setCurrentStep(prev => prev + 1);
  const goToPrev = () => setCurrentStep(prev => prev - 1);

  const openRefundModal = (orderId: string) => {
    setOrderId(orderId);
    setVisible(true);
  };

  const closeRefundModal = () => {
    setVisible(false);
    setCurrentStep(0);
    setSelectedIssue(null);
    setSelectedLineItems([]);
  };

  return (
    <RefundModalContext.Provider value={{ openRefundModal, closeRefundModal }}>
      {children}

      <Modal
        open={visible}
        onCancel={closeRefundModal}
        footer={null}
        destroyOnClose
        width={1000}
      >
        <div className="min-h-[400px]">
          {currentStep === 0 && (
            <IssueSelection
              onSelect={issue => {
                setSelectedIssue(issue);
                goToNext();
              }}
            />
          )}

          {currentStep === 1 && (
            <ProductSelection
              orderId={orderId}
              selectedLineItems={selectedLineItems}
              onSelect={setSelectedLineItems}
              onNext={goToNext}
              onBack={goToPrev}
            />
          )}

          {currentStep === 2 && (
            <RefundForm
              orderId={orderId}
              lineItems={selectedLineItems}
              issue={selectedIssue}
              setSelectedIssue={setSelectedIssue}
              onBack={goToPrev}
              onClose={closeRefundModal}
            />
          )}
        </div>
      </Modal>
    </RefundModalContext.Provider>
  );
};
